"use client";
import { useCallback, useEffect, useState, useRef } from "react";
import InfiniteScroll from "@/components/InfiniteScroll";
import apiFetch from "@/lib/fetch";
import { DEFAULT_DATA_LIMIT } from "@/@core/constants/pagination";
import VenueCard from "@/components/views/list/card/VenueCard";
import { VenueLoading } from "@/components/views/list/venue-list/Venue";
import { useCampaignContext } from "@/context/CampaignContext";
import debounce from "lodash/debounce";
import { useUserLocation } from "@/context/UserLocationContext";

interface Props extends SearchProps {
  data: any;
}

export default function VenuesOfCampaignClientSection(props: Props) {
  const [data, setData] = useState<any>(props.data);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const { campaignFilters } = useCampaignContext();

  const {
    locationPermission,
    userLocation,
    venueDistances,
    calculateDistances,
  } = useUserLocation();

  const calculatedVenueIds = useRef<Set<number>>(new Set());

  // Calculate distances when user location is available
  useEffect(() => {
    if (locationPermission === "granted" && userLocation && data?.length > 0) {
      const venuesToCalculate = data
        .filter((venue: any) => {
          const hasCoords = venue.map_latitude && venue.map_longitude;
          const notCalculated = !calculatedVenueIds.current.has(venue.id);
          return hasCoords && notCalculated;
        })
        .map((venue: any) => ({
          id: venue.id,
          lat: parseFloat(venue.map_latitude),
          lng: parseFloat(venue.map_longitude),
        }));

      if (venuesToCalculate.length > 0) {
        venuesToCalculate.forEach((v: { id: number }) => calculatedVenueIds.current.add(v.id));
        calculateDistances(venuesToCalculate);
      }
    }
  }, [locationPermission, userLocation, data, calculateDistances]);

  const fetchData = async (page) => {
    let url = `campaign/venues?index=${page}`;

    if (props.service) url += `&service=${props.service}`;
    if (props.district) url += `&district=${props.district}`;
    if (props.venue) url += `&venue=${props.venue}`;

    if (campaignFilters.service)
      url += `&campaignService=${campaignFilters?.service?.id}`;
    if (campaignFilters.district)
      url += `&campaignDistrict=${campaignFilters?.district?.id}`;

    return apiFetch({ route: url });
  };

  const next = async (page: number) => {
    if (isLoading) return;

    setIsLoading(true);
    const response = await fetchData(page);
    setIsLoading(false);

    if (response?.length === 0) setHasMore(false);
    else setHasMore(true);

    setData((prev: any) => {
      return [...prev, ...response];
    });
  };

  const fetchVenues = useCallback(
    debounce(async () => {
      setIsLoading(true);
      const response = await fetchData(0);
      setData(response);

      if (response?.length === 0) setHasMore(false);
      else setHasMore(true);

      setIsLoading(false);
    }, 300),
    [campaignFilters],
  );

  useEffect(() => {
    fetchVenues();
    return () => {
      fetchVenues.cancel();
    };
  }, [campaignFilters, fetchVenues]);

  return (
    <InfiniteScroll
      skeletonElementId="venue-card-skeleton"
      isLoading={isLoading}
      hasMore={hasMore}
      loading={<VenueLoading count={6} />}
      next={next}
      dataLength={data.response?.length || 0}
    >
      <div className="xl:grid justify-items-center grid-cols-1 flex flex-wrap justify-center xl:grid-cols-3 gap-5 py-4">
        {Array.isArray(data) &&
          data?.map((venue: Venue.List, index: number) => {
            const distance = venueDistances.get(venue.id);
            return <VenueCard titleHeadingLevel="h3" key={index} distance={distance} {...venue} />;
          })}
      </div>
    </InfiniteScroll>
  );
}
